import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  formatSearchGroupLabel,
  getSearchDestinationResults,
  getSearchStatus,
  resolveSearchRoutePath,
} from "@/components/layout/adminGlobalSearchModalHelpers";
import type { AdminRouteAccessArgs } from "@/routing/adminRoutes";
import { requestBroadcastExit } from "@/routing/broadcastLeaveGuard";
import { useLazySearchAdminV2Query } from "@/store/api/baseApi";
import type { AdminV2SearchGroup, JsonObject } from "@/types/adminV2";

interface AdminGlobalSearchModalProps {
  /** Text typed in the sidebar search before choosing "Search records". */
  initialQuery?: string;
  open: boolean;
  onClose: () => void;
  routeAccess: AdminRouteAccessArgs;
}

interface AdminGlobalSearchModalContentProps {
  initialQuery: string;
  onClose: () => void;
  routeAccess: AdminRouteAccessArgs;
}

const AdminGlobalSearchModalContent: React.FC<
  AdminGlobalSearchModalContentProps
> = ({ initialQuery, onClose, routeAccess }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [triggerSearch, searchState] = useLazySearchAdminV2Query();

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      return;
    }

    const timeout = setTimeout(() => {
      void triggerSearch({
        q: normalizedQuery,
        limit: 20,
      });
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [query, triggerSearch]);

  const groups = useMemo(
    () => searchState.data?.groups ?? [],
    [searchState.data],
  );
  const destinationResults = useMemo(
    () =>
      getSearchDestinationResults({
        query,
        routeAccess,
      }),
    [query, routeAccess],
  );
  const accessibleGroups = useMemo<AdminV2SearchGroup[]>(() => {
    return groups.flatMap((group) => {
      const items = group.items.filter(
        (item) =>
          resolveSearchRoutePath({
            groupType: group.type,
            itemId: item.id,
            metadata: item.metadata,
            routeAccess,
          }) !== null,
      );

      return items.length > 0
        ? [
            {
              ...group,
              count: items.length,
              items,
            },
          ]
        : [];
    });
  }, [groups, routeAccess]);
  const recordResultCount = accessibleGroups.reduce(
    (total, group) => total + group.count,
    0,
  );
  const resultCount = destinationResults.length + recordResultCount;
  const hasResults = resultCount > 0;
  const searchStatus = getSearchStatus({
    query,
    isFetching: searchState.isFetching && !hasResults,
    isError: searchState.isError && !hasResults,
    groupCount: resultCount,
  });

  const handleSelectDestination = (path: string) => {
    onClose();
    void requestBroadcastExit(() => navigate(path));
  };

  const handleSelectResult = (args: {
    groupType: string;
    itemId: string;
    metadata: JsonObject | null;
  }) => {
    const route = resolveSearchRoutePath({
      ...args,
      routeAccess,
    });
    if (route) {
      handleSelectDestination(route);
    }
  };

  return (
    <>
      <DialogTitle>Search admin</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            autoFocus
            label="Search admin"
            placeholder="Type at least 2 characters"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            fullWidth
          />

          {searchStatus === "startTyping" ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Search pages and records you can open.
            </Typography>
          ) : searchStatus === "searching" ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Searching admin...
            </Typography>
          ) : searchStatus === "error" ? (
            <Typography variant="body2" color="error">
              Search is unavailable right now. Try again.
            </Typography>
          ) : searchStatus === "empty" ? (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              No matching pages or records found.
            </Typography>
          ) : (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {resultCount} result
                {resultCount === 1 ? "" : "s"}
              </Typography>

              {destinationResults.length > 0 ? (
                <Stack spacing={1}>
                  <Divider />
                  <Typography variant="subtitle2">
                    Pages ({destinationResults.length})
                  </Typography>
                  <List disablePadding>
                    {destinationResults.map((destination) => (
                      <ListItemButton
                        key={destination.id}
                        onClick={() =>
                          handleSelectDestination(destination.path)
                        }
                      >
                        <ListItemText
                          primary={destination.label}
                          secondary={destination.description}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Stack>
              ) : null}

              {searchState.isError ? (
                <Typography variant="body2" color="error">
                  Record search is unavailable right now. Page results are still
                  available.
                </Typography>
              ) : searchState.isFetching ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Searching records...
                </Typography>
              ) : null}

              {accessibleGroups.map((group) => (
                <Stack key={group.type} spacing={1}>
                  <Divider />
                  <Typography variant="subtitle2">
                    {formatSearchGroupLabel(group.type)} ({group.count})
                  </Typography>
                  <List disablePadding>
                    {group.items.map((item) => (
                      <ListItemButton
                        key={`${group.type}:${item.id}`}
                        onClick={() =>
                          handleSelectResult({
                            groupType: group.type,
                            itemId: item.id,
                            metadata: item.metadata,
                          })
                        }
                      >
                        <ListItemText
                          primary={item.title}
                          secondary={item.subtitle ?? `ID: ${item.id}`}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Stack>
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
    </>
  );
};

export const AdminGlobalSearchModal: React.FC<AdminGlobalSearchModalProps> = ({
  initialQuery = "",
  open,
  onClose,
  routeAccess,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {open ? (
        <AdminGlobalSearchModalContent
          initialQuery={initialQuery}
          onClose={onClose}
          routeAccess={routeAccess}
        />
      ) : null}
    </Dialog>
  );
};

export default AdminGlobalSearchModal;
